/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    NvDialogButton,
    NvHigButtonLink,
    NvHigIconTextDescription,
    NvHigText,
    NvListItem
} from './hig-modal-shared.types';

export interface NvHigListItemData {
    core: NvHigListItemCoreData;
    lead?: NvHigListItemLeadData;
    trail?: NvHigListItemTrailData;
    context?: NvHigListInsertData;
}

export interface NvHigListItemLeadData {
    elementType: NvHigListItemLeadingElementType;
    icon?: string;
    image?: string;
    checkboxState?: boolean;
    primaryIcon?: string;
    badgeIcon?: string;
}

export interface NvHigListItemCoreData {
    headline: NvHigText;
    headlineTextStyle?: NvHigListItemHeadlineTextStyle;
    buttonLink?: NvHigButtonLink;
    supportingText?: NvHigIconTextDescription[];
}

export interface NvHigListItemTrailData {
    elementType: NvHigListItemTrailingElementType;
    icon?: string;
    ariaLabel?: string;
    toggleState?: boolean;
    primaryButton?: NvDialogButton;
    secondaryButton?: NvDialogButton;
    listItems?: NvListItem[];
    disabled?: boolean;
    dialogHeader?: NvHigText;
    plaintext?: NvHigText;
    rotaryLeftAriaLabel?: string;
    rotaryRightAriaLabel?: string;
    rotaryAriaLabel?: string;
    truncateRotaryText?: boolean;
}

export interface NvHigListItemContextData {
    context: NvHigIconTextDescription;
    primaryButton?: NvDialogButton;
    secondaryButton?: NvDialogButton;
}

export interface NvHigListInsertData {
    isLeftBorder?: boolean;
    leadIcon?: string;
    headerText?: NvHigText;
    supportingText?: NvHigText;
    primaryButtonText?: string;
    primaryButtonTextParams?: Record<string, string>;
    primaryButtonIcon?: string;
    secondaryButtonText?: string;
    iconButton?: string;
    colorTheme?: ColorThemes;
    /**
     * ARIA role for the container.
     * Use 'alert' for urgent messages requiring immediate attention.
     * Use 'status' for informational notifications.
     * Use 'region' with aria-label for general informational content.
     * @default undefined (no role)
     */
    ariaRole?: 'alert' | 'status' | 'region';
    /**
     * ARIA label for the alert/status region.
     * Required when ariaRole is 'region'.
     */
    ariaLabel?: string;
}

export enum NvHigListItemLeadingElementType {
    Icon,
    AvatarIcon,
    SquareImage,
    RectangularImage
}

export enum NvHigListItemTrailingElementType {
    Icon,
    Checkbox,
    Switch,
    IconButton,
    TextButton,
    Rotary,
    Dropdown,
    PlainText
}

export enum NvHigListItemHeadlineTextStyle {
    Body1 = 'font-body1',
    Body2 = 'font-body2'
}

export enum ColorThemes {
    White = 'theme-white',
    Red = 'theme-red',
    Yellow = 'theme-yellow',
    Blue = 'theme-blue',
    Green = 'theme-green',
    Default = ''
}
